// The HUD: DOM, not WebGL, so it is cheap to restyle and readable on a phone. Every method the game
// called before is here with the same name and signature; `setHole` takes an optional third argument
// (the built hole) to show the length, and there are two small additions (`setEstimate`, `setAccent`).
// In VR the browser hides all of this — scene/props.ts carries the same facts on boards by the tee.

import { CLUBS, CLUB_ORDER, type Club } from '@shared/physics';
import type { Hole } from '@shared/course';
import type { RoomPublicState } from '@shared/protocol';

/** Short labels for the club rack; the long ones live in CLUBS[club].label. */
const SHORT: Record<Club, string> = { driver: 'D', wood: '3W', iron5: '5i', iron7: '7i', iron9: '9i', wedge: 'W', putter: 'P' };

function el<T extends HTMLElement>(id: string): T {
  const e = document.getElementById(id);
  if (!e) throw new Error(`missing #${id}`);
  return e as T;
}
/** Optional: the fine-grain spans a restyle may drop without breaking the game. */
function q<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

export interface HudHandlers {
  aimLeft(): void;
  aimRight(): void;
  clubPrev(): void;
  clubNext(): void;
  swingStart(): void;
  swingEnd(): void;
  /** optional: the pointer left the button mid-charge — drop the swing rather than hit */
  swingCancel?(): void;
  askCaddie(): void;
  addBot(): void;
  copyLink(): void;
  /** optional: the club rack picks a club directly */
  setClub?(club: Club): void;
}

export class Hud {
  private log = el<HTMLDivElement>('hud-log');
  private caddie = el<HTMLDivElement>('hud-caddie');
  private scores = el<HTMLDivElement>('hud-scores');
  private powerFill = el<HTMLDivElement>('hud-power-fill');
  private swingBtn = el<HTMLButtonElement>('btn-swing');
  private rack = q<HTMLDivElement>('hud-club-rack');
  private club: Club = 'driver';

  constructor(h: HudHandlers) {
    el('btn-aim-left').onclick = h.aimLeft;
    el('btn-aim-right').onclick = h.aimRight;
    el('btn-club-prev').onclick = h.clubPrev;
    el('btn-club-next').onclick = h.clubNext;
    el('btn-caddie').onclick = h.askCaddie;
    el('btn-bot').onclick = h.addBot;
    el('btn-link').onclick = h.copyLink;
    const down = (e: Event) => { e.preventDefault(); h.swingStart(); };
    const up = (e: Event) => { e.preventDefault(); h.swingEnd(); };
    const cancel = (e: Event) => { e.preventDefault(); (h.swingCancel ?? h.swingEnd)(); };
    this.swingBtn.addEventListener('pointerdown', down);
    this.swingBtn.addEventListener('pointerup', up);
    this.swingBtn.addEventListener('pointerleave', cancel);
    this.swingBtn.addEventListener('pointercancel', cancel);

    // The rack is built here so the club list lives in one place (CLUB_ORDER).
    if (this.rack) {
      for (const club of CLUB_ORDER) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'club';
        b.dataset.club = club;
        b.textContent = SHORT[club];
        b.title = CLUBS[club].label;
        b.onclick = () => { h.setClub ? h.setClub(club) : this.stepTo(club, h); };
        this.rack.appendChild(b);
      }
    }
  }

  /** Fallback for a host that did not pass setClub: walk the rack with prev/next. */
  private stepTo(club: Club, h: HudHandlers) {
    let guard = CLUB_ORDER.length;
    while (this.club !== club && guard-- > 0) {
      if (CLUB_ORDER.indexOf(club) > CLUB_ORDER.indexOf(this.club)) h.clubNext();
      else h.clubPrev();
      this.club = CLUB_ORDER[(CLUB_ORDER.indexOf(this.club) + (CLUB_ORDER.indexOf(club) > CLUB_ORDER.indexOf(this.club) ? 1 : -1) + CLUB_ORDER.length) % CLUB_ORDER.length];
    }
  }

  /** The hole's accent colour drives the flag, the tee markers and every live element of the HUD. */
  setAccent(hex: string) {
    document.documentElement.style.setProperty('--accent', hex);
  }

  setRoom(id: string, name: string, status: string) {
    const s = q('hud-room-status'); if (s) s.textContent = status;
    const i = q('hud-room-id'); if (i) i.textContent = id;
    const n = q('hud-room-name'); if (n) n.textContent = name;
  }

  setHole(state: RoomPublicState, myId: string | null, hole?: Hole | null) {
    const brief = state.spec.holes[state.hole];
    const turnEl = q('hud-hole-turn');
    if (!brief) {
      const name = q('hud-hole-name'); if (name) name.textContent = 'round over';
      if (turnEl) turnEl.textContent = 'card is closed';
      return;
    }
    const turn = state.players.find((p) => p.id === state.turn);
    const mine = !!turn && turn.id === myId;
    const set = (id: string, v: string) => { const e = q(id); if (e) e.textContent = v; };
    set('hud-hole-index', String(state.hole + 1));
    set('hud-hole-count', String(state.spec.holes.length));
    set('hud-hole-name', brief.name);
    set('hud-hole-par', String(brief.par));
    set('hud-hole-theme', brief.theme);
    // tee to flag, which is the length a golfer means; lengthM is the terrain, 24–32 m longer
    set('hud-hole-length', hole ? `${Math.round(Math.hypot(hole.cup.x - hole.tee.x, hole.cup.z - hole.tee.z))} m` : '');
    if (turnEl) {
      turnEl.textContent = state.status === 'finished' ? 'round over' : !turn ? 'between holes' : mine ? 'your shot' : `${turn.name} to play`;
      turnEl.classList.toggle('mine', mine);
    }
    const quip = q('hud-hole-quip');
    if (quip) quip.textContent = brief.quip ?? '';
  }

  /* ── the lobby and the round card (2026-09-13) ──
     The hand-off dropped a player on the tee with no way in and, at the end, a HUD that read
     "card is closed". Two overlays: the room before the first shot — who is here, the invite
     link, Dex, tee off — and the card when the round is finished, with a way to a new round. */
  overlay: 'lobby' | 'round' | 'notice' | null = null;
  private overlayEl(): HTMLDivElement {
    let o = q<HTMLDivElement>('links-overlay');
    if (o) return o;
    o = document.createElement('div');
    o.id = 'links-overlay';
    o.style.cssText = 'position:absolute;inset:0;display:grid;place-items:center;pointer-events:auto;background:radial-gradient(70% 60% at 50% 45%, rgba(8,11,6,.55), rgba(8,11,6,.86));z-index:5;';
    (q('hud') ?? document.body).appendChild(o);
    return o;
  }
  hideOverlay() { q('links-overlay')?.remove(); this.overlay = null; }
  private btn(act: string, label: string, primary = false) {
    return `<button type="button" data-act="${act}" style="pointer-events:auto;cursor:pointer;min-height:44px;padding:.6em 1.1em;margin:0 .5em .5em 0;border-radius:2px;font:inherit;font-size:.78em;letter-spacing:.16em;text-transform:uppercase;${primary ? 'background:var(--accent,#e0b13c);color:#14180f;border:1px solid var(--accent,#e0b13c);font-weight:700;' : 'background:rgba(12,16,9,.5);color:rgba(242,239,230,.85);border:1px solid rgba(242,239,230,.25);'}">${label}</button>`;
  }
  private card(kind: 'lobby' | 'round' | 'notice', inner: string, onAct: (act: string, el: HTMLButtonElement) => void) {
    this.overlay = kind;
    const o = this.overlayEl();
    o.innerHTML = `<div style="width:min(92vw,560px);max-height:88vh;overflow:auto;padding:1.6em 1.8em 1.4em;border:1px solid rgba(224,177,60,.45);border-radius:4px;background:linear-gradient(180deg,rgba(20,26,15,.97),rgba(12,16,9,.98));box-shadow:0 40px 80px -30px rgba(0,0,0,.9);color:#f2efe6;font-size:clamp(13px,0.5vw+9px,16px);font-family:Archivo,system-ui,sans-serif;">${inner}</div>`;
    o.querySelectorAll<HTMLButtonElement>('[data-act]').forEach((b) => b.addEventListener('click', () => onAct(b.dataset.act ?? '', b)));
  }
  showLobby(state: RoomPublicState, myId: string | null, onAct: (act: string, el: HTMLButtonElement) => void) {
    const holes = state.spec.holes.map((b, i) => `<tr><td style="padding:.25em .8em .25em 0;color:rgba(242,239,230,.55);">${i + 1}</td><td style="padding:.25em .8em .25em 0;font-family:'Instrument Serif',Georgia,serif;font-size:1.15em;">${escapeHtml(b.name)}</td><td style="padding:.25em .8em .25em 0;">par ${b.par}</td><td style="padding:.25em 0;color:rgba(242,239,230,.6);">${escapeHtml(b.theme)}</td></tr>`).join('');
    const players = state.players.map((p) => `<li style="margin:.15em 0;">${escapeHtml(p.name)}${p.id === myId ? ' <span style="color:rgba(242,239,230,.55);">(you)</span>' : ''}${p.kind === 'bot' ? ' <span style="color:rgba(242,239,230,.55);">· the house player</span>' : ''}${p.connected ? '' : ' <span style="color:rgba(242,239,230,.4);">· away</span>'}</li>`).join('');
    const hasBot = state.players.some((p) => p.kind === 'bot');
    const nHoles = state.spec.holes.length;
    this.card('lobby',
      `<div style="font-family:'Instrument Serif',Georgia,serif;font-size:2.2em;line-height:1;letter-spacing:.06em;">LINKS</div>`
      + `<div style="margin:.4em 0 1em;font-size:.72em;letter-spacing:.16em;text-transform:uppercase;color:rgba(242,239,230,.55);">room ${escapeHtml(state.id)} · ${nHoles} hole${nHoles === 1 ? '' : 's'} · up to ${state.maxPlayers ?? 4} players · ${state.open ? 'an open table' : 'private, by this link only'}</div>`
      + `<table style="border-collapse:collapse;margin-bottom:1em;">${holes}</table>`
      + `<div style="font-size:.72em;letter-spacing:.16em;text-transform:uppercase;color:rgba(242,239,230,.55);">At the tee</div><ul style="margin:.3em 0 1em;padding-left:1.1em;line-height:1.5;">${players}</ul>`
      + `<div style="margin:0 0 .8em;font-size:.9em;line-height:1.5;color:rgba(242,239,230,.8);">Send the link and a friend lands on this tee with you. Hold <b>Swing</b> (or the space bar) to charge, release to hit; on the green the meter is scaled to the putt and the line shows the break. The ball you watch is the one the server computed.</div>`
      + `<div style="margin:0 0 1em;font-size:.85em;line-height:1.5;color:rgba(242,239,230,.6);"><b style="color:rgba(242,239,230,.8);">Built for a headset, not yet tried on one:</b> in the Quest browser, tee off, then press <b>Enter VR</b> — hold the trigger or pinch, swing, let go; on a Quest 3, <b>On the table (AR)</b> puts the course on your real table. Every headset path has been proven with a simulated device, none yet through real lenses.</div>`
      + `<div>${this.btn('tee', 'Tee off', true)}${this.btn('copy', 'Copy invite link')}${hasBot ? '' : this.btn('bot', 'Seat Dex')}</div>`
      + `<div style="margin-top:.6em;"><a href="https://mypuzzle.club/games/" style="display:inline-block;min-height:44px;line-height:44px;color:rgba(242,239,230,.6);font-size:.8em;">← All the games</a></div>`,
      onAct);
  }
  showRoundCard(state: RoomPublicState, myId: string | null, onAct: (act: string, el: HTMLButtonElement) => void) {
    const pars = state.spec.holes.map((b) => b.par);
    const totalPar = pars.reduce((a, b) => a + b, 0);
    const head = `<tr><td></td>${state.spec.holes.map((_, i) => `<td style="padding:.2em .6em;text-align:center;color:rgba(242,239,230,.55);">${i + 1}</td>`).join('')}<td style="padding:.2em .6em;text-align:center;color:rgba(242,239,230,.55);">total</td></tr>`;
    const parRow = `<tr><td style="padding:.2em .8em .2em 0;color:rgba(242,239,230,.55);">par</td>${pars.map((p) => `<td style="padding:.2em .6em;text-align:center;">${p}</td>`).join('')}<td style="padding:.2em .6em;text-align:center;">${totalPar}</td></tr>`;
    const totals = state.players.map((p) => ({ p, tot: p.strokes.reduce((a, b) => a + b, 0) }));
    const rows = totals.map(({ p, tot }) => `<tr><td style="padding:.2em .8em .2em 0;font-family:'Instrument Serif',Georgia,serif;font-size:1.2em;">${escapeHtml(p.name)}${p.id === myId ? ' (you)' : ''}</td>${p.strokes.map((s) => `<td style="padding:.2em .6em;text-align:center;font-variant-numeric:tabular-nums;">${s || '–'}</td>`).join('')}<td style="padding:.2em .6em;text-align:center;font-variant-numeric:tabular-nums;font-weight:700;">${tot} <span style="color:var(--accent,#e0b13c);font-size:.8em;">(${tot - totalPar > 0 ? '+' : ''}${tot - totalPar})</span></td></tr>`).join('');
    const best = Math.min(...totals.map((t) => t.tot));
    const winners = totals.filter((t) => t.tot === best).map((t) => (t.p.id === myId ? 'You' : t.p.name));
    const verdict = totals.length < 2 ? `${best - totalPar === 0 ? 'Level par.' : best - totalPar < 0 ? `${totalPar - best} under.` : `${best - totalPar} over.`}` : winners.length > 1 ? `All square between ${winners.join(' and ')}.` : `${winners[0]} win${winners[0] === 'You' ? '' : 's'}.`;
    this.card('round',
      `<div style="font-size:.72em;letter-spacing:.16em;text-transform:uppercase;color:rgba(242,239,230,.55);">Round over · room ${escapeHtml(state.id)}</div>`
      + `<div style="font-family:'Instrument Serif',Georgia,serif;font-size:2em;line-height:1.1;margin:.2em 0 .6em;">${verdict}</div>`
      + `<div style="overflow:auto;"><table style="border-collapse:collapse;margin-bottom:1em;min-width:100%;">${head}${parRow}${rows}</table></div>`
      /* no "Copy link" here: this room is over, and a copied address dropped a friend on an
         empty HUD reading "this round is over" (QC round 2). New round is the door. */
      + `<div>${this.btn('new', 'New round', true)}</div>`
      + `<div style="margin-top:.6em;"><a href="https://mypuzzle.club/games/" style="display:inline-block;min-height:44px;line-height:44px;color:rgba(242,239,230,.6);font-size:.8em;">← All the games</a></div>`,
      onAct);
  }

  /** A visitor who cannot be seated — the round is over, the room is full, or the address is
   *  wrong — gets told so on a card with a way onward, not a line in the tape. */
  showNotice(message: string, onAct: (act: string, el: HTMLButtonElement) => void) {
    const why = /over/.test(message) ? 'This round is over.' : /full/.test(message) ? 'This room is full.' : /no such room|not initialised/.test(message) ? 'There is no room at this address.' : message;
    this.card('notice',
      `<div style="font-size:.72em;letter-spacing:.16em;text-transform:uppercase;color:rgba(242,239,230,.55);">Links</div>`
      + `<div style="font-family:'Instrument Serif',Georgia,serif;font-size:2em;line-height:1.1;margin:.2em 0 .6em;">${escapeHtml(why)}</div>`
      + `<div style="margin:0 0 1em;font-size:.9em;line-height:1.5;color:rgba(242,239,230,.8);">A room lives for one round. Start a fresh one and send its link, or go back to the games.</div>`
      + `<div>${this.btn('new', 'Start a new round', true)}</div>`
      + `<div style="margin-top:.6em;"><a href="https://mypuzzle.club/games/" style="display:inline-block;min-height:44px;line-height:44px;color:rgba(242,239,230,.6);font-size:.8em;">← All the games</a></div>`,
      onAct);
  }

  setScores(state: RoomPublicState, myId: string | null) {
    const body = q<HTMLTableSectionElement>('hud-scores-body') ?? this.scores;
    const n = state.spec.holes.length;
    const round = q('hud-scores-round');
    if (round) round.textContent = `${n} hole${n === 1 ? '' : 's'}`;
    const cell = (v: string | number, cls = '') => `<td${cls ? ` class="${cls}"` : ''}>${v}</td>`;
    const parRow = `<tr class="par">${cell('PAR')}${state.spec.holes.map((b) => cell(b.par)).join('')}${cell(state.spec.holes.reduce((a, b) => a + b.par, 0), 'tot')}</tr>`;
    const rows = state.players.map((p) => {
      const turn = p.id === state.turn;
      const total = p.strokes.reduce((a, b) => a + b, 0);
      const tag = p.kind === 'bot' ? '' : p.id === myId ? ' (you)' : '';
      const cells = Array.from({ length: n }, (_, i) => cell(p.strokes[i] || '–')).join('');
      const marker = turn ? '<i>▸</i>' : '<i class="ghost">▸</i>';
      const name = `${marker}${escapeHtml(p.name)}${tag}${p.connected ? '' : ' ·'}`;
      return `<tr class="${turn ? 'turn' : ''}">${cell(name, 'who')}${cells}${cell(`<b>${total}</b>`, 'tot')}</tr>`;
    }).join('');
    body.innerHTML = parRow + rows;
  }

  setShot(club: Club, aimDeg: number, myTurn: boolean, distanceToCupM: number | null, baseAimDeg: number | null = null) {
    this.club = club;
    const set = (id: string, v: string) => { const e = q(id); if (e) e.textContent = v; };
    set('hud-shot-club', CLUBS[club].label);
    // shown relative to the line to the flag when that line is known: 0.0° is straight at it, + is right
    let rel = baseAimDeg === null ? aimDeg : aimDeg - baseAimDeg;
    while (rel > 180) rel -= 360; while (rel < -180) rel += 360;
    set('hud-shot-aim', distanceToCupM !== null && distanceToCupM < 0.2 ? '–' : `${baseAimDeg !== null && rel > 0 ? '+' : ''}${rel.toFixed(1)}°`);
    set('hud-shot-dist', distanceToCupM === null ? '–' : String(Math.round(distanceToCupM)));
    this.swingBtn.disabled = !myTurn;
    this.rack?.querySelectorAll<HTMLButtonElement>('.club').forEach((b) => {
      b.classList.toggle('on', b.dataset.club === club);
    });
  }

  /** What the club would carry at the power now on the meter; a hint, not a promise. A shot that
   *  would leave the property says so, rather than printing a number that will not happen. */
  setEstimate(est: { metres: number; penalty: boolean } | number | null) {
    const e = q('hud-shot-est');
    if (!e) return;
    if (est === null) { e.textContent = '–'; return; }
    const metres = typeof est === 'number' ? est : est.metres;
    const penalty = typeof est === 'number' ? false : est.penalty;
    e.textContent = `${Math.round(metres)}${penalty ? ' · out' : ''}`;
  }

  setWind(w: { x: number; z: number }) {
    const speed = Math.hypot(w.x, w.z);
    const s = q('hud-wind-speed');
    if (s) s.textContent = speed.toFixed(1);
    const arrow = q('hud-wind-arrow');
    // 0° is down the hole, toward the flag; the dial reads like a compass on the tee.
    if (arrow) arrow.style.transform = `rotate(${(Math.atan2(w.x, w.z) * 180) / Math.PI + 180}deg)`;
  }

  setPower(p: number) {
    const pct = Math.round(Math.max(0, Math.min(1, p)) * 100);
    this.powerFill.style.width = `${pct}%`;
    const needle = q('hud-power-needle');
    if (needle) needle.style.left = `${pct}%`;
    const label = q('hud-power-pct');
    if (label) label.textContent = `${pct}%`;
  }

  say(text: string, source: 'ai' | 'placeholder', who = 'Dex') {
    const t = q('hud-caddie-text');
    if (!t) { this.caddie.textContent = text; return; }
    t.textContent = text;
    const w = q('hud-caddie-who'); if (w) w.textContent = who;
    // The placeholder label is part of the honesty of the thing: no key, no pretending.
    const s = q('hud-caddie-src'); if (s) s.textContent = source === 'placeholder' ? 'placeholder · no ai key' : 'ai';
    this.caddie.classList.remove('in');
    void this.caddie.offsetWidth;
    this.caddie.classList.add('in');
  }

  note(text: string) {
    const line = document.createElement('div');
    line.textContent = text;
    this.log.appendChild(line);
    while (this.log.childElementCount > 40) this.log.removeChild(this.log.firstChild!);
    this.log.scrollTop = this.log.scrollHeight;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
}
